import type { ITextTypeSettings } from '@/types/watermarks.types';

type TiledTextProps = {
  text: string;
  height: number;
  width: number;
  padding: number;
  rotation: number;
  textSettings: ITextTypeSettings;
  scale: number;
};

const tileCache = new Map<string, Blob>();
const MAX_CACHE = 100;
function trimMap() {
  if (tileCache.size <= MAX_CACHE) return tileCache;

  // Get an iterator of all keys
  const keys = tileCache.keys();

  // Loop through keys and remove until size is correct
  while (tileCache.size > MAX_CACHE) {
    tileCache.delete(keys.next().value as string);
  }
  return tileCache;
}

function setTileCache(key: string, blob: Blob) {
  tileCache.set(key, blob);
  trimMap();
}

export const generateTiledText = async (props: TiledTextProps) => {
  const key = JSON.stringify(props);
  if (tileCache.has(key)) {
    return URL.createObjectURL(tileCache.get(key) as Blob);
  }

  const { text, height, width, padding, rotation, textSettings, scale } = props;
  const { font, font_size, color } = textSettings;
  const size = Math.sqrt(width ** 2 + height ** 2);
  let _font = `${font_size}px 'Inter'`;

  let fontName = font.name.replace(/'"/g, ''); // drop quotes from input font, will replace them lower down

  if (!fontName) {
    fontName = 'Inter';
  }

  if (!fontName.includes('Inter')) {
    try {
      await document.fonts.load(`${font_size}px '${fontName}'`);
      _font = `${font_size}px '${fontName}'`;
    } catch (e) {
      console.error('Failed to load font for tiled text', e);
    }
  }

  const canvas = new OffscreenCanvas(size * 2, size * 2);
  const context = canvas.getContext('2d', { alpha: true });
  if (!context) return null;

  // must set font before rendering text
  context.font = _font;
  const textWidth = context.measureText(text).width;
  const tileW = textWidth + padding;
  const tileH = font_size + padding;

  // const halfDiagonal = Math.ceil(Math.sqrt(width * width + height * height));
  const halfX = Math.ceil(size / tileW) + 1;
  const halfY = Math.ceil(size / tileH) + 1;

  context.fillStyle = color;
  context.strokeStyle = '#0000003f';
  context.lineWidth = scale;
  context.textBaseline = 'top';
  context.translate(size, size);
  context.rotate((rotation * Math.PI) / 180);

  for (let i = -halfX; i <= halfX; i++) {
    for (let ii = -halfY; ii <= halfY; ii++) {
      const stagger = i % 2 !== 0 ? tileH * 0.5 : 0;
      context.fillText(text, i * tileW, ii * tileH + stagger);
      context.strokeText(text, i * tileW, ii * tileH + stagger);
    }
  }

  const blob = await canvas.convertToBlob();
  const url = URL.createObjectURL(blob);
  setTileCache(key, blob);
  return url;
};
